import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { saveAs } from 'file-saver';
type requestTypes = "video";
type clientHooks = "onComplete" | "onRequest" | "onConnect" | "onDisconnect";
export interface ClientHook {
  id: string;
  hook: string;
}

export interface AudioFilesInterface {
  url: string;
  startTime: string;
  endTime: string;
}

export interface VideoEncoderRequestInterface {
  id: string;
  socketId?: string;
  userIdentifier: string;
  rawMp4?: boolean;
  videoData: {
    url: string;
    fps: number;
    duration: number;
    audioFiles?: Array<AudioFilesInterface>;
    viewport?: {
      width: number;
      height: number;
    };
    selector?: string;
    left?: number;
    top?: number;
  };
}

export interface VideoEncoderBatchRequestInterface {
  items: Array<VideoEncoderRequestInterface>
}

export interface VideoEncoderResponse extends AxiosResponse {
  data: {
    requestId: string;
  };
}

export interface VideoPreviewResponse extends AxiosResponse {
  data: any;
}

interface ClientConfigObject {
  requiresClient: boolean;
}
export default class Client {
  private client: AxiosInstance;

  private configObject: ClientConfigObject;
  private authToken: string;
  private requiresAuthentication: boolean;
  private requiresSocket: boolean;
  constructor(
    private url: string,
    private socketId?: string,
    private hooks: Array<ClientHook> = []
  ) {
    this.configObject = {
      requiresClient: false,
    };

    this.client = axios.create({
      baseURL: this.url,
    });

    this.requiresSocket = false;
  }

  getUrl(uri: string = '') {
    return this.url + '/' + uri;
  }

  getAuthToken() {
    return this.authToken;
  }

  requiresClient(requiresClient: boolean = true) {
    this.configObject.requiresClient = requiresClient;
  }

  setSocket(socketId: string) {
    this.requiresSocket = true;
    this.socketId = socketId;
  }

  getSocketId() {
    return this.socketId;
  }

  addHook(hook: ClientHook) {
    this.hooks.push(hook);
  }

  private getHeaders(): AxiosRequestConfig {
    if (this.authToken) {
      return {
        headers: {
          Authorization: "Bearer " + this.authToken,
        },
      };
    }

    return null;
  }

  private executeEncoding(encoderDataBody: VideoEncoderRequestInterface) {
    let url = process.env.ENV == 'dev' ? "/queue" : "encode/queue"; // Quick hack to have the api gateway disabled for local development
    //@ts-ignore
    if(window.experimentalEncoder == true) {
      url = "testencode/queue";
    }
    return this.client.post<any, VideoEncoderResponse>(
      url,
      encoderDataBody,
      this.getHeaders()
    );
  }

  private executeBatchEncoder(encoderDataBody: VideoEncoderBatchRequestInterface) {
    let url = process.env.ENV == 'dev' ? "/queue/batch" : "encode/queue/batch"; // Quick hack to have the api gateway disabled for local development
    //@ts-ignore
    if(window.experimentalEncoder == true) {
      url = "testencode/queue/batch";
    }
    return this.client.post<any, VideoEncoderResponse>(
      url,
      encoderDataBody,
      this.getHeaders()
    );
  }

  private async executePreview(encoderDataBody: VideoEncoderRequestInterface) {
    await this.waitForRequestPreparation();
    const url = process.env.ENV == 'dev' ? "/preview" : "preview/preview"; // Quick hack to have the api gateway disabled for local development
    return this.client.post<any, VideoPreviewResponse>(
      url,
      encoderDataBody,
      this.getHeaders()
    );
  }

  private async waitForRequestPreparation() {

    const checkSocket = () => {
      if(this.requiresSocket) {
        return this.socketId !== undefined;
      }

      return true;
    }
    const assertIfRequestIsReady = (resolve: any, reject: any) => {
      const allChecksPass = (): boolean => {
        if (this.requiresAuthentication == true) {
          return this.authToken !== undefined && checkSocket();
        } else {
          return checkSocket();
        }
      };

      if (allChecksPass()) {
        resolve();
        return true;
      }

      setTimeout(() => {
        assertIfRequestIsReady(resolve, reject);
      }, 500);
    };

    return new Promise((resolve, reject) => {
      assertIfRequestIsReady(resolve, reject);
    });
  }

  async encode(encoderDataBody: VideoEncoderRequestInterface) {
    await this.waitForRequestPreparation();
    encoderDataBody.userIdentifier = this.getSocketId();
    return this.executeEncoding(encoderDataBody);
  }

  async encodeBatch(encoderBatchDataBody: VideoEncoderBatchRequestInterface) {
    await this.waitForRequestPreparation();

    // Attach the user identifier for socket id
    encoderBatchDataBody.items.forEach(item => {
      item.userIdentifier = this.getSocketId();
    });
    
    return this.executeBatchEncoder(encoderBatchDataBody);
  }

  async authenticate() {
    this.requiresAuthentication = true;
    console.log("Authenticating brush encoder");
    return axios({
      url: "/custom-code/php/microservices/generate-auth-token.php",
    }).then((resp) => {
      this.authToken = resp.data.token;
    }).catch(err => {
      console.error("[ERROR WHILE AUTHENTICATING BRUSH CLIENT]", err);
    });
  }

  async enableNotification(requestId: string, notificationProviderId: number, notificationEmail: string) {
    await this.waitForRequestPreparation();
    const url = process.env.ENV == 'dev' ? `/notifications/enable/${requestId}` : `encode/notifications/enable/${requestId}`;

    return this.client.post(
      url,
      {
        notificationEmail,
        notificationProviderId
      }
    );
  }

  async download(requestId: string, extension: string = "mp4", callback: (response: AxiosResponse<any>) => any = undefined) {
    await this.waitForRequestPreparation();
    const headers = this.getHeaders()?.headers;
    axios({
      url: process.env.HOST + "/files/" + requestId,
      headers,
      method: "GET",
      responseType: "blob", // important
    }).then((response) => {
      
      if(callback) {
        return callback(response);
      } 
      
      const blob = new Blob([response.data]);
      const fileName = requestId + '.' + extension;
      saveAs(blob, fileName);
  
    });
  }

  async getPreview(
    encoderDataBody: VideoEncoderRequestInterface,
    targetId?: string
  ) {
    await this.waitForRequestPreparation();
    encoderDataBody.userIdentifier = this.getSocketId();
    return this.executePreview(encoderDataBody).then((response) => {
      if (targetId) {
        try {
          const image: HTMLImageElement = <HTMLImageElement>(
            document.getElementById(targetId)
          );
          image.src = response.data;
        } catch (err) {
          console.error("Could not draw preview to image", err);
        }
      }
      return response;
    });
  }
}

let client: Client = null;

export const getClient = (socketId?: string) => {
  if (!client) {
    client = new Client(process.env.HOST);
  }

  if (socketId) {
    client.setSocket(socketId);
  }

  return client;
};
